import type { Gtm } from '~~/types/@nuxtjs/gtm';

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
  }
}

const buildGtmScript = (gtmId: string): string =>
  `window.dataLayer=window.dataLayer||[];`
  + `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});`
  + `var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';`
  + `j.async=true;j.src='/metrics123/?id='+i+dl;`
  + `f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${gtmId}');`;

const buildGtmNoscript = (gtmId: string): string =>
  `<iframe src="/metrics123/?id=${gtmId}"`
  + ` height="0" width="0" style="display:none;visibility:hidden"></iframe>`;

export default defineNuxtPlugin(() => {

  const config = useRuntimeConfig();
  const enabled = config.public.gtmEnabled;
  const gtmId = String(config.public.gtmId || '');
  const debug = config.public.gtmDebug;

  // Ensure dataLayer exists early so any pushes before the GTM script loads are queued safely.
  // This plugin only runs on the client (see .client.ts suffix), so `window` is always defined.
  window.dataLayer = window.dataLayer || [];

  if (!gtmId) {

    console.log('[GTM] No GTM ID found in runtime config. GTM will not be initialized.');

  } else if (!enabled) {

    console.log(`[GTM] GTM is disabled (id: ${gtmId}). Set NUXT_PUBLIC_GTM_ENABLED=true to enable.`);

  } else {

    // Inject the standard GTM snippet as raw markup so Cloudflare's Google Tag Gateway
    // can detect the canonical https://www.googletagmanager.com/gtm.js?id=... URL and
    // rewrite it (and all subsequent GTM/GA4 traffic) through the first-party proxy.
    useHead({
      script: [
        {
          innerHTML: buildGtmScript(gtmId),
          tagPosition: 'head',
        },
      ],
      noscript: [
        {
          innerHTML: buildGtmNoscript(gtmId),
          tagPosition: 'bodyOpen',
        },
      ],
    });

    // Preserve the automatic {event: 'content-view'} push on every route change that
    // the previous @gtm-support/vue-gtm plugin performed via its `vueRouter` option.
    const router = useRouter();

    router.afterEach(() => {

      window.dataLayer?.push({ event: 'content-view' });

    });

    console.log(`[GTM] Initialized with id: ${gtmId}, debug: ${debug}`);

  }

  const gtm: Gtm = {
    push: (payload: Record<string, unknown>) => {

      if (!enabled || !gtmId) {

        return;

      }
      window.dataLayer?.push(payload);

    },
  };

  return {
    provide: {
      gtmClient: gtm,
    },
  };

});
